import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBookingStore } from '../stores/bookingStore';
import { useAuthStore } from '../stores/authStore';
import { reservationApi } from '../api/reservationApi';
import { pageSEO } from '../utils/seo';

const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { reset } = useBookingStore();
  const [, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);
  const seo = pageSEO.paymentSuccess;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // 이미 처리했으면 중복 실행 방지
    if (hasProcessed.current) {
      return;
    }

    const processPayment = async () => {
      const paymentKey = searchParams.get('paymentKey');
      const orderId = searchParams.get('orderId');
      const amount = searchParams.get('amount');

      if (!paymentKey || !orderId || !amount) {
        setError(t('payment.invalidInfo', '결제 정보가 올바르지 않습니다.'));
        setIsProcessing(false);
        return;
      }

      // 처리 시작 플래그 설정
      hasProcessed.current = true;

      try {
        // 예약 정보를 가져와서 확인
        const bookingState = useBookingStore.getState();

        // 필수 데이터가 없으면 에러 처리
        if (!bookingState.reservationData || !bookingState.selectedVehicle) {
          setError(t('payment.missingReservation', '예약 정보를 찾을 수 없습니다. 다시 예약해주세요.'));
          setIsProcessing(false);
          return;
        }

        // 결제 세부 항목은 bookingStore에 이미 저장되어 있음
        const paymentBreakdown = bookingState.paymentBreakdown;

        if (!paymentBreakdown) {
          setError(t('payment.missingPayment', '결제 정보를 찾을 수 없습니다. 다시 예약해주세요.'));
          setIsProcessing(false);
          return;
        }

        // 로그인한 사용자 정보 가져오기
        const authState = useAuthStore.getState();
        const userId = authState.isAuthenticated && authState.user ? authState.user.id : undefined;

        // 백엔드 API로 예약 생성 요청 (결제 정보 포함)
        // 이 API 호출이 GoodTime API (aJax.Reservation.asp)를 호출하여 실제 예약을 생성합니다
        const reservation = await reservationApi.createReservation({
          vehicle_idx: bookingState.selectedVehicle.idx || bookingState.selectedVehicle.ids?.[0] || '',
          vehicle_name: bookingState.selectedVehicle.vehicle_name || bookingState.selectedVehicle.model_name,
          vehicle_class: bookingState.selectedVehicle.class || bookingState.selectedVehicle.car_class || '',
          vehicle_number: bookingState.selectedVehicle.license_plate,
          user_id: userId, // 회원이면 user_id 저장, 비회원이면 undefined
          user_name: bookingState.reservationData.name,
          user_phone: bookingState.reservationData.phone,
          user_email: bookingState.reservationData.email,
          user_birth_date: bookingState.reservationData.birthDate, // 생년월일 추가
          license_number: '',
          start_date: bookingState.startDateTime!.toISOString(),
          end_date: bookingState.endDateTime!.toISOString(),
          pickup_location: bookingState.reservationData.pickupLocation,
          return_location: bookingState.reservationData.returnLocation,
          total_amount: parseInt(amount),
          has_own_insurance: bookingState.reservationData.hasOwnInsurance || 'N',
          special_request: bookingState.reservationData.specialRequest || '',
          payment_method: 'card',
          payment_key: paymentKey,
          order_id: orderId,
          payment_breakdown: paymentBreakdown,
          user_coupon_id: bookingState.selectedCoupon?.user_coupon_id,
        });

        setIsProcessing(false);

        // 성공 페이지로 리다이렉트
        navigate('/success', {
          state: {
            reservationData: bookingState.reservationData,
            vehicle: bookingState.selectedVehicle,
            startDateTime: bookingState.startDateTime,
            endDateTime: bookingState.endDateTime,
            totalAmount: parseInt(amount),
            reservation_number: reservation.reservation_number,
            paymentKey,
            orderId,
            paymentBreakdown: paymentBreakdown,
            selectedCoupon: bookingState.selectedCoupon,
          },
          replace: true,
        });

        // 스토어 초기화
        reset();
      } catch (err) {
        console.error('Payment confirmation error:', err);
        setError(t('payment.confirmFailed', '결제 승인 처리 중 오류가 발생했습니다.'));
        setIsProcessing(false);
      }
    };

    processPayment();
  }, [searchParams, navigate, reset, t]);

  if (error) {
    return (
      <>
        <Helmet>
          <title>{seo.title}</title>
          <meta name="description" content={seo.description} />
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('payment.processingFailed')}</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            {t('common.home')}
          </button>
        </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
        <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('payment.processing')}</h1>
        <p className="text-gray-600">{t('payment.pleaseWait')}</p>
      </div>
      </div>
    </>
  );
};

export default PaymentSuccessPage;
